import { useSelector } from 'react-redux'

import { getFilteredThemes } from './shared'
import usePrevious from '../../../utils/hooks/usePrevious'

export type ThemeFilter = {
  groupName: string
  multi: boolean
  filters: Array<{ id: string; attribute: string; isDisabled: boolean }>
}

export function useThemeFilters(activeThemeFilterTags: string[]) {
  const themes = useSelector<State, Core.Theme[]>((state: State) => state.get('themes').toJS())
  const previousActiveThemeFilterTags = usePrevious(activeThemeFilterTags)

  const themeFilters = themes.reduce<ThemeFilter[]>((filterGroups, theme) => {
    Object.entries(theme.attributes || []).forEach(([groupName, attributeValue]) => {
      const multi = typeof attributeValue !== 'string'
      const attributes = multi ? attributeValue : [attributeValue]

      attributes.forEach((attribute: string) => {
        const filterItem = { id: `${groupName}:${attribute}`, attribute, isDisabled: false }
        const filterGroup = filterGroups.find((item) => item.groupName === groupName)

        if (!filterGroup) {
          filterGroups.push({ groupName, multi, filters: [filterItem] })
        } else if (!filterGroup.filters.some((item) => item.attribute === attribute)) {
          filterGroup.filters.push(filterItem)
        }
      })
    })

    return filterGroups
  }, [])

  // Sort the filters alphabetically for each group
  themeFilters.forEach((themeFilter) => {
    themeFilter.filters.sort((a, b) => a.attribute.localeCompare(b.attribute))
  })

  if (activeThemeFilterTags.length) {
    // Added or removed a filter from the active theme filter selection:
    // Update all theme filter disabled states
    const activeMultiSelectionThemeFilterTags = activeThemeFilterTags.filter((tag) =>
      themeFilters.some((themeFilter) => themeFilter.multi && themeFilter.groupName === tag.split(':')[0]),
    )

    themeFilters.forEach((themeFilter) => {
      themeFilter.filters.forEach((filter) => {
        const themeFilterTag = `${themeFilter.groupName}:${filter.attribute}`
        const themeFilterTags = [...activeThemeFilterTags, themeFilterTag]
        const singleSelectionThemeFilterTags = themeFilterTags.filter(
          (tag) => !activeMultiSelectionThemeFilterTags.includes(tag),
        )

        filter.isDisabled =
          !activeThemeFilterTags.some((tag) => tag === themeFilterTag) &&
          getFilteredThemes(themes, themeFilterTags).every(
            (theme) =>
              !singleSelectionThemeFilterTags.every((tag) => {
                const [groupName, attribute] = tag.split(':')
                return [theme.attributes?.[groupName]].flat().includes(attribute)
              }),
          )
      })
    })
  } else if (previousActiveThemeFilterTags?.length) {
    // Cleared the active theme filter selection
    themeFilters.forEach((themeFilter) => {
      themeFilter.filters.forEach((filter) => {
        filter.isDisabled = false
      })
    })
  }

  return themeFilters
}
